<template>
  <div>
    <PageHeaderBanner>
      <template #title>在庫価格ファイル取込フォーマット指定</template>
    </PageHeaderBanner>
    <MessageChip :display="isMessageChip" level="E" :message="errMessage" />
    <q-form
      greedy
      @submit="displayRegisterDialog"
      @reset="clearData"
      @validation-error="displayErrMsg"
    >
      <div class="cmd00200-input-form q-gutter-y-md">
        <div class="row q-mb-sm">
          <div class="column stock-price-file-name">
            <label>在庫価格ファイル名 </label>
            <span class="q-ml-sm">
              {{ fileName }}
            </span>
          </div>
          <div class="column file-size q-pl-sm">
            <label>ファイルサイズ</label>
            <span class="q-ml-sm">
              {{ fileSize }}
            </span>
          </div>
        </div>
        <div class="row q-gutter-x-sm q-mb-lg">
          <div class="col">
            <label class="requier">サプライヤ名</label>
            <SupplierSelects
              :supplier="supplier"
              :is-validate="true"
              @select-supplier="supplier = $event"
            />
          </div>
          <div class="col">
            <label class="requier">カテゴリー</label>
            <CategorySelects
              :category="category"
              :is-validate="true"
              @select-category="category = $event"
            />
          </div>
          <div class="col-1.5 cmd00200-template-button">
            <q-btn
              color="primary"
              style="width: 200px; height: 42px"
              :class="supplier && category ? '' : 'cmd00200-disabled-btn'"
              label="テンプレートを読み込む"
              :disable="supplier && category ? false : true"
              @click="readTemplete"
            />
            <q-tooltip
              v-if="supplier && category ? false : true"
              anchor="top middle"
              self="bottom middle"
              :offset="[1, 1]"
            >
              <strong>サプライヤ名とカテゴリーを選択してください</strong>
            </q-tooltip>
          </div>
        </div>
        <div class="col">
          <label class="requier">文字コード</label>
          <div class="q-gutter-sm">
            <q-radio v-model="characterCode" val="1" label="UTF-8" />
            <q-radio v-model="characterCode" val="0" label="Shift-JIS" />
          </div>
        </div>
        <div class="col">
          <label class="requier">ヘッダ行の有無</label>
          <div class="q-gutter-sm">
            <q-radio v-model="headerLine" val="0" label="有" />
            <q-radio v-model="headerLine" val="1" label="無" />
          </div>
        </div>
        <div class="col">
          <label class="text-weight-bold" style="font-size: 1.1rem">項目名設定</label>
        </div>
        <div
          :class="
            'cmd00200-item-table chem-table-area ' + (rows.length === 0 ? 'table-nodata' : '')
          "
        >
          <q-table
            :rows="rows"
            :columns="colums"
            row-key="number"
            flat
            separator="cell"
            hide-pagination
            :rows-per-page-options="[rows.length]"
          >
            <template #body="cell">
              <q-tr>
                <q-td key="number" class="text-right" style="width: 1.5rem">
                  {{ cell.row.number }}
                </q-td>
                <q-td key="system_item_name">
                  <label :class="cell.row.number === 1 && 'requier'">
                    {{ cell.row.system_item_name }}
                  </label>
                </q-td>
                <q-td style="min-width: 280px">
                  <FormatItemSelects
                    :number="cell.row.number"
                    :value="cell.row.format_item_value"
                    :error="cell.row.error"
                    :options="formatItemOptions"
                    @select-item="cell.row.format_item_value = $event"
                  />
                </q-td>
              </q-tr>
            </template>
          </q-table>
          <div class="col" style="margin-top: 2px">
            <q-checkbox v-model="addTempleteFlag">テンプレートとして登録する</q-checkbox>
          </div>
        </div>
        <div class="row">
          <label class="text-weight-bold" style="font-size: 1.1rem">価格設定</label>
        </div>
        <div class="col q-mt-sm q-mb-lg">
          <label class="requier">価格単位</label>
          <q-select
            v-model="priceUnit"
            :options="priceOptions"
            outlined
            dense
            emit-value
            map-options
            style="background: var(--q-white); padding: 0; width: 30%"
            :rules="[(val) => !!val || $t('validation.required')]"
            @update:model-value="priceUnit === 'JPY' ? (commission = '0') : (commission = '0.00')"
          />
        </div>
        <div class="col q-mt-sm q-mb-sm price-input">
          <label class="requier">割引率</label>
          <q-input
            v-model="discountRate"
            outlined
            dense
            :rules="[
              (val) => isEmpty(val) || $t('validation.required'),
              (val) =>
                !(val < 0.0 || val > 1.0) ||
                $t('validation.between', { param1: '0.00', param2: '1.00' }),
            ]"
            lazy-rules
            hint="0.00～1.00で入力してください"
            mask="#.##"
            style="width: 30%"
            input-class="text-right"
            reverse-fill-mask
            maxlength="4"
            @keydown.enter.prevent
            @keypress.enter.prevent
          />
        </div>
        <div class="col q-mt-sm price-input">
          <label class="requier">手数料</label>
          <q-input
            v-model="commission"
            outlined
            dense
            :rules="
              priceUnit === 'JPY'
                ? [
                    (val) => isEmpty(val) || $t('validation.required'),
                    (val) => isNumeric(val) || $t('validation.integer_only'),
                  ]
                : [
                    (val) => isEmpty(val) || $t('validation.required'),
                    (val) => isDecimalDigits(val) || $t('validation.numeric_only'),
                    (val) => isTwoDecimalPlaces(val) || $t('validation.two_decimal_places'),
                    (val) =>
                      parseInt(val, 10) < 100000000 ||
                      $t('validation.max_integer', { param1: '8' }),
                  ]
            "
            lazy-rules
            :suffix="priceOptions.find((data) => data.value === priceUnit)?.label"
            style="width: 30%"
            input-class="text-right"
            :maxlength="priceUnit === 'JPY' ? 8 : 11"
            @keydown.enter.prevent
          />
        </div>
        <div class="row">
          <label class="text-weight-bold" style="font-size: 1.1rem">その他設定</label>
        </div>
        <div class="col q-mt-sm">
          <label class="requier">過去データ</label>

          <div class="q-gutter-sm">
            <q-radio v-model="pastData" val="1" label="削除する" />
            <q-radio v-model="pastData" val="0" label="削除しない" />
          </div>

          <div>
            <p class="q-mb-none">
              ※「削除する」を選択した場合、選択したサプライヤとカテゴリーに紐づく過去に取り込んだすべての在庫価格が削除されます。
            </p>
          </div>
        </div>
        <div class="col q-mt-md">
          <label>備考</label>
          <q-input
            v-model="memo"
            outlined
            dense
            type="textarea"
            rows="2"
            bg-color="white"
            counter
            maxlength="200"
          />
        </div>
        <div class="col q-mt-sm">
          <label class="requier">取込予定日時</label>
          <div class="q-gutter-sm flex">
            <q-radio v-model="scheduleType" val="1" label="即時実行" />
            <div class="flex">
              <q-radio v-model="scheduleType" val="0" label="処理日時指定" class="q-mr-sm" />

              <div class="row q-gutter-x-sm q-ml-sm">
                <q-input
                  v-model="scheduleDate"
                  outlined
                  dense
                  :disable="scheduleType == '1'"
                  class="date q-pb-none"
                  readonly
                  style="background: var(--q-white); width: 140px"
                  clearable
                >
                  <template #append>
                    <q-icon name="event" class="cursor-pointer"></q-icon>
                  </template>
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-date v-model="scheduleDate" minimal no-unset mask="YYYY/MM/DD">
                      <div class="row items-center justify-end">
                        <q-btn v-close-popup label="OK" color="primary" flat />
                      </div>
                    </q-date>
                  </q-popup-proxy>
                </q-input>

                <q-input
                  v-model="scheduleTime"
                  outlined
                  :disable="scheduleType == '1'"
                  dense
                  class="date q-pb-none"
                  readonly
                  style="background: var(--q-white); width: 126px"
                  clearable
                >
                  <template #append>
                    <q-icon name="access_time" class="cursor-pointer"></q-icon>
                  </template>
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-time v-model="scheduleTime" mask="HH:mm" :format24h="false">
                      <div class="row items-center justify-end">
                        <q-btn v-close-popup label="OK" color="primary" flat />
                      </div>
                    </q-time>
                  </q-popup-proxy>
                </q-input>
              </div>
            </div>
          </div>
        </div>
        <FormFooterBanner style="position: inherit">
          <template #button-area>
            <div class="row">
              <q-btn
                color="grey-7"
                outline
                label="戻る"
                style="min-width: 120px; background: var(--q-white) !important"
                @click="backPage"
              />
              <div class="col q-gutter-x-xs flex justify-end">
                <q-btn type="submit" color="primary" style="width: 150px" label="登録" />
                <q-btn
                  type="reset"
                  outline
                  style="
                    width: 150px;
                    background: var(--q-white) !important;
                    color: var(--q-delete);
                  "
                  icon="mdi-eraser"
                  label="クリア"
                />
              </div>
            </div>
          </template>
        </FormFooterBanner>
      </div>
    </q-form>
    <!-- エラーダイアログ -->
    <AlertDialog
      :display="isFileFormatDialogFlg"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="backPage"
      @close="backPage"
    />
    <!-- テンプレート読込ダイアログ -->
    <AlertDialog
      :display="isTemplateDialogFlg"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isTemplateDialogFlg = false"
      @close="isTemplateDialogFlg = false"
    />
    <!-- 登録ダイアログ -->
    <AlertDialog
      :display="isRegisterDialogFlg"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="backPage"
      @confirm="registerStockPriceCsv"
      @close="isRegisterDialogFlg = false"
    />
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Loading } from 'quasar';
import axios from '../../common/axios-common';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import {
  isEmpty,
  isNumeric,
  isTwoDecimalPlaces,
  isDecimalDigits,
} from '../../common/utils/validateUtils';

import MessageChip from '../../components/chip/MessageChip.vue';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import SupplierSelects from '../../components/selects/SupplierSelects.vue';
import CategorySelects from '../../components/selects/CategorySelects.vue';
import FormatItemSelects from '../../components/selects/FormatItemSelects.vue';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import FormFooterBanner from '../../components/banner/FormFooterBanner.vue';
import messages from '../../messages/message.json';

interface Props {
  // CSVファイル名
  fileName: string;
}

// 在庫価格項目名プルダウン
interface FormatItemOption {
  label: string;
  value: string;
}

// 在庫価格項目名
interface StockPriceItemName {
  number: number;
  system_item_name: string;
  system_item_key: string;
  format_item_value: string;
  error: boolean;
}

const router = useRouter();
const props = defineProps<Props>();

// 在庫価格ファイル名
const fileName = ref('');
// ファイルサイズ
const fileSize = ref('');

// サプライヤ名
const supplier = ref('');
// カテゴリー
const category = ref('');

// 文字コード
const characterCode = ref('1');
// ヘッダ行の有無
const headerLine = ref('0');
// 過去データ
const pastData = ref('1');

// 価格単位
const priceUnit = ref('USD');
// 割引率
const discountRate = ref('0.00');
// 手数料
const commission = ref('0.00');

// 価格単位プルダウン
const priceOptions = ref<FormatItemOption[]>([
  {
    label: 'USD',
    value: 'USD',
  },
  {
    label: 'EUR',
    value: 'EUR',
  },
  {
    label: 'GBP',
    value: 'GBP',
  },
  {
    label: 'JPY',
    value: 'JPY',
  },
]);

// 「テンプレートとして登録する」フラグ
const addTempleteFlag = ref(false);

// 備考
const memo = ref('');

// 処理スケジュールタイプ(0:処理日時指定 1:即時実行)
const scheduleType = ref('1');
// スケジュール日付
const scheduleDate = ref('');
// スケジュール日付(リセット用)
const resetScheduleDate = ref('');
// スケジュール時間
const scheduleTime = ref('');
// スケジュール時間(リセット用)
const resetScheduleTime = ref('');

// 在庫価格ファイル項目名プルダウン
const formatItemOptions = ref<FormatItemOption[]>([]);

// Dialogタイトル
const dialogTitle = ref('');
// Dialog表示レベル
const dialogLevel = ref('');
// Dialog本文
const dialogText = ref(['']);
// 確認Dialogフラグ
const isConfFlg = ref(false);
// テンプレートDialog表示フラグ
const isTemplateDialogFlg = ref(false);
// 登録Dialog表示フラグ
const isRegisterDialogFlg = ref(false);
// CSV文字コードエラーDialog表示フラグ
const isFileFormatDialogFlg = ref(false);

// エラーメッセージ
const errMessage = ref('');
// MessageChip表示フラグ
const isMessageChip = ref(false);

// 項目名設定カラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const colums: Array<any> = [
  {
    name: 'number',
    label: '番号',
    align: 'left',
    field: (row: StockPriceItemName) => row.number,
    classes: 'text-right',
  },
  {
    name: 'system_item_name',
    label: 'システム取込項目名',
    align: 'left',
    field: 'system_item_name',
  },
  {
    name: 'format_item_name',
    label: '在庫価格ファイル項目名',
    align: 'left',
    field: 'format_item_name',
    headerClasses: 'stock-price-file-name',
  },
];

// 項目名設定テーブルデータ
const rows = ref<StockPriceItemName[]>([
  {
    number: 1,
    system_item_name: 'サプライヤ商品コード',
    system_item_key: 'id_number',
    format_item_value: '',
    error: false,
  },
  {
    number: 2,
    system_item_name: '化学名',
    system_item_key: 'name',
    format_item_value: '',
    error: false,
  },
  {
    number: 3,
    system_item_name: '在庫状況',
    system_item_key: 'stock_status',
    format_item_value: '',
    error: false,
  },
  {
    number: 4,
    system_item_name: '容量',
    system_item_key: 'capacity',
    format_item_value: '',
    error: false,
  },
  {
    number: 5,
    system_item_name: '荷姿',
    system_item_key: 'packing',
    format_item_value: '',
    error: false,
  },
  {
    number: 6,
    system_item_name: '仕入価格',
    system_item_key: 'purchase_price',
    format_item_value: '',
    error: false,
  },
  {
    number: 7,
    system_item_name: '納期',
    system_item_key: 'delivery_date',
    format_item_value: '',
    error: false,
  },
  {
    number: 8,
    system_item_name: '備考',
    system_item_key: 'remarks',
    format_item_value: '',
    error: false,
  },
  {
    number: 9,
    system_item_name: '保管温度',
    system_item_key: 'storage_temperature',
    format_item_value: '',
    error: false,
  },
  {
    number: 10,
    system_item_name: '純度',
    system_item_key: 'purity',
    format_item_value: '',
    error: false,
  },
  {
    number: 11,
    system_item_name: 'MDL',
    system_item_key: 'mdl',
    format_item_value: '',
    error: false,
  },
  {
    number: 12,
    system_item_name: 'CAS No.',
    system_item_key: 'cas',
    format_item_value: '',
    error: false,
  },
  {
    number: 13,
    system_item_name: 'URL',
    system_item_key: 'url',
    format_item_value: '',
    error: false,
  },
]);

/**
 * データクリア
 */
const clearData = () => {
  headerLine.value = '0';
  characterCode.value = '0';
  pastData.value = '1';
  supplier.value = '';
  category.value = '';
  memo.value = '';
  addTempleteFlag.value = false;
  priceUnit.value = 'USD';
  discountRate.value = '0.00';
  commission.value = '0.00';
  scheduleType.value = '1';
  scheduleDate.value = resetScheduleDate.value;
  scheduleTime.value = resetScheduleTime.value;
  errMessage.value = '';
  isMessageChip.value = false;
  // 在庫価格ファイル項目名プルダウンのクリア
  rows.value.forEach((value, index) => {
    rows.value[index].format_item_value = '';
    rows.value[index].error = false;
  });
};

/**
 * 在庫価格ファイル取込(CSV)画面に戻る
 */
const backPage = () => {
  router.push({ name: 'stockPrice' });
};

/**
 * 登録確認ダイアログ表示
 */
const displayRegisterDialog = () => {
  dialogTitle.value = 'スケジュール登録確認';
  dialogLevel.value = 'information';
  dialogText.value = [`入力した内容で取込スケジュールを登録します。`, 'よろしいですか？'];
  isConfFlg.value = false;
  isRegisterDialogFlg.value = true;
};

/**
 * 登録完了ダイアログ
 */
const completeRegisterDialog = () => {
  dialogTitle.value = 'スケジュール登録完了';
  dialogLevel.value = 'information';
  dialogText.value = ['スケジュールの登録が完了しました。'];
  isConfFlg.value = true;
  isRegisterDialogFlg.value = true;
};

/**
 * エラーメッセージ表示
 */
const displayErrMsg = () => {
  errMessage.value = messages.ja.message.MSG_50070E;
  isMessageChip.value = true;
  // TOPにスクロール
  setTimeout(() => window.scrollTo({ top: 0 }), 400);
};

/**
 * 初期表示データ取得
 */
const getInitData = () => {
  // Loading Start
  Loading.show();
  const formData = new FormData();
  formData.append('file_name', props.fileName);

  axios
    .post('/cmd002001/init', formData)
    .then((res) => {
      const { common, csvInfo, currentDate, currentTime } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // 各項目に設定
          fileName.value = csvInfo.file_name;
          fileSize.value = csvInfo.file_size;
          scheduleDate.value = currentDate;
          resetScheduleDate.value = currentDate;
          scheduleTime.value = currentTime;
          resetScheduleTime.value = currentTime;
          // 在庫価格ファイル項目名プルダウンを設定
          Object.keys(csvInfo.csv_name_list).forEach((key) => {
            let label;
            // 35文字以上の場合は省略
            if (csvInfo.csv_name_list[key].length > 35) {
              label = `${csvInfo.csv_name_list[key].substr(0, 35)}...`;
            } else {
              label = csvInfo.csv_name_list[key];
            }
            formatItemOptions.value.push({
              label,
              value: key,
            });
          });
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            // ファイル形式エラーの場合
            case DetailResultStatusCode.FILE_FORMAT_ERROR:
              // エラーダイアログ表示
              dialogTitle.value = 'ファイル読み込みエラー';
              dialogLevel.value = 'error';
              dialogText.value = [
                'CSVファイルの内容に誤りがあります。',
                '内容を確認して、再度CSVファイルをアップロードしてください。',
              ];
              isConfFlg.value = true;
              isFileFormatDialogFlg.value = true;
              break;

            // ファイルが存在しない場合
            case DetailResultStatusCode.FILE_NOT_EXIST_ERROR:
              dialogTitle.value = 'ファイル読み込みエラー';
              dialogLevel.value = 'error';
              dialogText.value = [common.message];
              isConfFlg.value = true;
              isFileFormatDialogFlg.value = true;
              break;

            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * テンプレート読み込み
 */
const readTemplete = () => {
  // Loading Start
  Loading.show();
  // エラーリセット
  errMessage.value = '';
  isMessageChip.value = false;
  // 在庫価格ファイル項目名プルダウンのクリア
  rows.value.forEach((value, index) => {
    rows.value[index].format_item_value = '';
    rows.value[index].error = false;
  });
  const formData = new FormData();
  formData.append('supplier_code', supplier.value);
  formData.append('category_id', category.value);

  axios
    .post('/cmd002001/read-template', formData)
    .then((res) => {
      const { common, template } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // テンプレート設定
          characterCode.value = String(template.character_code);
          headerLine.value = String(template.header);
          Object.entries(template).forEach(([key, value]) => {
            rows.value.forEach((item, index) => {
              // システム取込項目名と在庫価格CSVテンプレートの項目名が一致した場合
              if (item.system_item_key === key) {
                formatItemOptions.value.forEach((v) => {
                  if (v.value === String(value)) {
                    // 在庫価格ファイル項目名プルダウンを設定
                    rows.value[index].format_item_value = String(value);
                  }
                });
              }
            });
          });
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            // テンプレートが未登録の場合
            case DetailResultStatusCode.TEMPLATE_NOT_EXIST_ERROR:
              // 「テンプレートを登録する」をチェック状態にする
              addTempleteFlag.value = true;
              // 警告ダイアログ表示
              dialogTitle.value = 'テンプレート未登録';
              dialogLevel.value = 'warning';
              dialogText.value = [common.message];
              isConfFlg.value = true;
              isTemplateDialogFlg.value = true;
              break;

            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * 在庫価格CSV登録
 */
const registerStockPriceCsv = () => {
  // Loading Start
  Loading.show();
  // エラーリセット
  errMessage.value = '';
  isMessageChip.value = false;
  isRegisterDialogFlg.value = false;
  rows.value.forEach((value, index) => {
    rows.value[index].error = false;
  });

  // 登録データ設定
  const formData = new FormData();
  formData.append('file_name', fileName.value);
  formData.append('supplier_code', supplier.value);
  formData.append('category_id', category.value);
  formData.append('character_code', characterCode.value);
  formData.append('header_line', headerLine.value);
  // 価格設定
  formData.append('price_unit', priceUnit.value);
  formData.append('discount_rate', discountRate.value);
  if (priceUnit.value === 'JPY') {
    formData.append('commission', `${commission.value}.00`);
  } else {
    commission.value = Number(commission.value).toFixed(2);
    formData.append('commission', commission.value);
  }
  // その他設定
  formData.append('past_data', pastData.value);
  formData.append('memo', memo.value);
  // 取込予定日時
  formData.append('schedule_type', scheduleType.value);
  formData.append('schedule_date', scheduleDate.value);
  formData.append('schedule_time', scheduleTime.value);
  // 項目名設定
  rows.value.forEach((value) => {
    if (value.format_item_value) {
      formData.append(`stock_price_file_map[${value.system_item_key}]`, value.format_item_value);
    } else {
      // プルダウンが選択されていない場合は0を設定
      formData.append(`stock_price_file_map[${value.system_item_key}]`, '0');
    }
  });
  formData.append('add_templete_flag', addTempleteFlag.value ? '1' : '0');

  axios
    .post('/cmd002001/register-csv-file', formData)
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // 完了ダイアログ表示
          completeRegisterDialog();
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            // サプライヤ商品コードが選択されていない場合
            case DetailResultStatusCode.IDNUMBER_NOT_EXIST_ERROR:
              errMessage.value = common.message;
              isMessageChip.value = true;
              rows.value.forEach((value, index) => {
                if (value.system_item_key === 'id_number') {
                  rows.value[index].error = true;
                }
              });
              break;
            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
      // TOPにスクロール
      setTimeout(() => window.scrollTo({ top: 0 }), 400);
    });
};

/**
 * 初期表示処理
 */
// propsが存在しない場合
if (!props.fileName) {
  // 前画面に遷移
  backPage();
} else {
  // 初期表示データ取得
  getInitData();
}
</script>
<style scoped>
.cmd00200-input-form {
  width: 70%;
  margin: 0 auto;
}

.cmd00200-template-button {
  display: flex;
  align-items: flex-start;
  padding-top: 21px;
  margin: 0 0 0 20px;
}

.cmd00200-table {
  width: 90%;
  margin: 0 auto 30px;
}

.cmd00200-input-form .cmd00200-item-table.chem-table-area {
  margin-top: 4px;
}
</style>
<style>
.cmd00200-item-table .q-table__container {
  margin: 0px auto !important;
}

.cmd00200-item-table .stock-price-file-name {
  width: 480px;
  min-width: 480px;
  max-width: 480px;
}

.cmd00200-input-form .q-field--outlined.q-field--readonly .q-field__control:before {
  border-style: solid;
}

.cmd00200-template-button .cmd00200-disabled-btn {
  background-color: var(--q-opacityDisable) !important;
}

.cmd00200-input-form .q-field--dense .q-field__marginal {
  min-height: 42px;
}

.cmd00200-input-form .stock-price-file-name {
  width: 65%;
  margin: 0;
}

.cmd00200-input-form .stock-price-file-name span {
  word-break: break-all;
}

.cmd00200-input-form .file-size {
  width: 33%;
  margin-left: 2%;
}

.cmd00200-input-form .price-input .q-field--dense .q-field__marginal {
  min-height: 40px;
}
</style>
